import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { RfqPackage, RfqProject, RfqSubmission, TimberSupplierStructureRef } from '../../types';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { parseQuoteTotalInput, submissionQuoteTotal } from '../../utils/rfqQuoteTotal';
import { updateCncQuoteSubmission } from '../../utils/rfqCncQuoteEdit';
import { recordSupplierBillForRfqAward } from '../../utils/supplierBillIngestion';
import { approvedSubmissionIdsOnRfqProject } from '../../utils/rfqApprovedQuoteCoverage';
import { PRE_PORTAL_RELEASE_ID } from '../../utils/timberRfqPrePortalVersion';
import {
  normalizeQuotedStructures,
  timberQuoteStructurePickerOptions,
} from '../../utils/timberQuoteStreamKey';
import { loadDealProposalStructures } from '../../utils/loadDealProposalStructures';
import './LegacyQuoteEditModal.css';

export type CncQuoteSubmissionEditModalProps = {
  open: boolean;
  onClose: () => void;
  submission: RfqSubmission | null;
  orgName: string;
  project: RfqProject;
  orgId: string;
  projectPackages: RfqPackage[];
  selectedReleaseId: string | null;
  fallbackStructures?: TimberSupplierStructureRef[];
  onSuccess?: () => void;
};

const CncQuoteSubmissionEditModal: React.FC<CncQuoteSubmissionEditModalProps> = ({
  open,
  onClose,
  submission,
  orgName,
  project,
  orgId,
  projectPackages,
  selectedReleaseId,
  fallbackStructures,
  onSuccess,
}) => {
  const { user } = useAuth();
  const { updateRfqSubmission } = useCRM();
  const [quoteTotal, setQuoteTotal] = useState('');
  const [flatFee, setFlatFee] = useState('');
  const [ratePerBdft, setRatePerBdft] = useState('');
  const [leadTimeWeeks, setLeadTimeWeeks] = useState('');
  const [selectedStructureIds, setSelectedStructureIds] = useState<string[]>([]);
  const [loadedProposalStructures, setLoadedProposalStructures] = useState<TimberSupplierStructureRef[]>(
    [],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !project.dealId) {
      setLoadedProposalStructures([]);
      return;
    }
    if (fallbackStructures?.length) {
      setLoadedProposalStructures(fallbackStructures);
      return;
    }
    let cancelled = false;
    void (async () => {
      const structures = await loadDealProposalStructures(project.dealId, { hubProjectId: project.timberProjectId });
      if (!cancelled) setLoadedProposalStructures(structures);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, project.dealId, fallbackStructures]);

  const structureOptions = useMemo(() => {
    const pkgForOrg =
      projectPackages.find(
        (p) =>
          p.rfqProjectId === project.id
          && p.supplierOrgId === orgId
          && (!selectedReleaseId
            || selectedReleaseId === PRE_PORTAL_RELEASE_ID
            || p.hubFileReleaseId === selectedReleaseId),
      )
      || projectPackages.find((p) => p.rfqProjectId === project.id && p.supplierOrgId === orgId);
    const proposalFallback = fallbackStructures?.length
      ? fallbackStructures
      : loadedProposalStructures;
    return timberQuoteStructurePickerOptions(project, orgId, pkgForOrg, proposalFallback);
  }, [
    project,
    orgId,
    projectPackages,
    selectedReleaseId,
    fallbackStructures,
    loadedProposalStructures,
  ]);

  useEffect(() => {
    if (!open || !submission) return;
    const total = submissionQuoteTotal(submission);
    setQuoteTotal(total != null ? String(total) : '');
    setFlatFee(submission.cncQuote?.flatFee != null ? String(submission.cncQuote.flatFee) : '');
    setRatePerBdft(
      submission.cncQuote?.ratePerBdft != null ? String(submission.cncQuote.ratePerBdft) : '',
    );
    setLeadTimeWeeks(
      submission.cncQuote?.leadTimeWeeks != null ? String(submission.cncQuote.leadTimeWeeks) : '',
    );
    const quoted = normalizeQuotedStructures(submission.quotedStructures);
    if (quoted.length) {
      setSelectedStructureIds(quoted.map((s) => s.id));
    } else if (structureOptions.length === 1) {
      setSelectedStructureIds([structureOptions[0]!.id]);
    } else {
      setSelectedStructureIds([]);
    }
    setError(null);
  }, [open, submission, structureOptions]);

  if (!open || !submission?.id) return null;

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) {
      setError('You must be signed in.');
      return;
    }
    const total = parseQuoteTotalInput(quoteTotal);
    if (!total) {
      setError('Enter a valid total quote amount.');
      return;
    }
    const cncQuote: Record<string, number> = {};
    if (flatFee.trim()) {
      const v = Number(flatFee);
      if (!Number.isFinite(v) || v < 0) {
        setError('Flat fee must be a valid number.');
        return;
      }
      cncQuote.flatFee = v;
    }
    if (ratePerBdft.trim()) {
      const v = Number(ratePerBdft);
      if (!Number.isFinite(v) || v < 0) {
        setError('Rate per bdft must be a valid number.');
        return;
      }
      cncQuote.ratePerBdft = v;
    }
    if (leadTimeWeeks.trim()) {
      const v = Number(leadTimeWeeks);
      if (!Number.isFinite(v) || v < 0 || !Number.isInteger(v)) {
        setError('Lead time must be whole weeks.');
        return;
      }
      cncQuote.leadTimeWeeks = v;
    }

    let quotedStructures: TimberSupplierStructureRef[] | undefined;
    if (structureOptions.length > 0) {
      if (structureOptions.length >= 2 && selectedStructureIds.length === 0) {
        setError('Select at least one structure this quote applies to.');
        return;
      }
      quotedStructures = selectedStructureIds.length
        ? normalizeQuotedStructures(
            structureOptions.filter((s) => selectedStructureIds.includes(s.id)),
          )
        : [];
    }

    setBusy(true);
    setError(null);
    try {
      await updateCncQuoteSubmission(
        updateRfqSubmission,
        submission,
        {
          quoteTotal: total,
          cncQuote: Object.keys(cncQuote).length ? cncQuote : undefined,
          ...(quotedStructures !== undefined ? { quotedStructures } : {}),
        },
        { uid: user.id, name: user.name || user.email || 'Staff' },
      );
      if (submission.id && approvedSubmissionIdsOnRfqProject(project).has(submission.id)) {
        const names = (quotedStructures ?? submission.quotedStructures ?? [])
          .map((row) => row.name)
          .filter(Boolean);
        await recordSupplierBillForRfqAward({
          project,
          category: 'cnc',
          orgId,
          orgName,
          submissionKey: submission.id,
          amount: total,
          description: names.length ? `CNC quote — ${names.join(', ')}` : `CNC quote — ${orgName}`,
          byUserId: user.id,
          byUserName: user.name || user.email || 'Staff',
        });
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save quote input.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lqi-modal-backdrop" role="presentation" onClick={handleClose}>
      <div
        className="lqi-modal lqi-modal--timber"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cnc-quote-edit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lqi-modal-head">
          <div>
            <h2 id="cnc-quote-edit-title">Edit supplier quote input</h2>
            <p className="lqi-modal-sub">
              Correct CNC quote numbers and structure scope for {orgName}. Updates timeline and forecast coverage; does not re-send the RFQ. Saves are internal-only and marked as PM adjusted input.
            </p>
          </div>
          <button type="button" className="lqi-modal-close" onClick={handleClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <form className="lqi-modal-body" onSubmit={(ev) => void handleSubmit(ev)}>
          {structureOptions.length > 0 && (
            <fieldset className="lqi-field" disabled={busy} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
              <legend style={{ fontSize: 12, fontWeight: 600 }}>Structures this quote applies to</legend>
              <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 400, color: '#64748b' }}>
                Pick the structure(s) for this PDF so multiple uploads on the same supplier do not share one label.
              </p>
              {structureOptions.map((s) => (
                <label key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
                  <input
                    type="checkbox"
                    checked={selectedStructureIds.includes(s.id)}
                    onChange={() =>
                      setSelectedStructureIds((prev) =>
                        prev.includes(s.id) ? prev.filter((id) => id !== s.id) : [...prev, s.id],
                      )
                    }
                  />
                  <span>{s.name || s.id}</span>
                </label>
              ))}
            </fieldset>
          )}
          <label className="lqi-field">
            <span>Total quote ($)</span>
            <input
              type="text"
              inputMode="decimal"
              value={quoteTotal}
              disabled={busy}
              required
              onChange={(e) => setQuoteTotal(e.target.value)}
            />
          </label>
          <div className="lqi-species-quote-line">
            <label className="lqi-field lqi-field--inline">
              <span>Flat fee ($)</span>
              <input
                type="text"
                inputMode="decimal"
                value={flatFee}
                disabled={busy}
                onChange={(e) => setFlatFee(e.target.value)}
              />
            </label>
            <label className="lqi-field lqi-field--inline">
              <span>Rate / bdft ($)</span>
              <input
                type="text"
                inputMode="decimal"
                value={ratePerBdft}
                disabled={busy}
                onChange={(e) => setRatePerBdft(e.target.value)}
              />
            </label>
            <label className="lqi-field lqi-field--inline">
              <span>Lead time (wk)</span>
              <input
                type="number"
                min={0}
                step={1}
                value={leadTimeWeeks}
                disabled={busy}
                onChange={(e) => setLeadTimeWeeks(e.target.value)}
              />
            </label>
          </div>
          {error && <p className="lqi-error">{error}</p>}
          <div className="lqi-modal-actions">
            <button type="button" className="lqi-btn lqi-btn--ghost" disabled={busy} onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="lqi-btn lqi-btn--primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CncQuoteSubmissionEditModal;
